import React, { createContext, use, useCallback, useMemo, useState } from 'react';

// Types
interface QuizResults {
    [key: string]: any;
}

interface QuizState {
    results: QuizResults;
    count: number;
}

interface QuizContextValue extends QuizState {
    setResults: (results: QuizResults) => void;
    incrementCount: () => void;
    clearQuiz: () => void;
}

// Context
const QuizContext = createContext<QuizContextValue | undefined>(undefined);

// Provider Props
interface QuizProviderProps {
    children: React.ReactNode;
    results?: QuizResults;
    count?: number;
}

// Provider Component
export const QuizProvider: React.FC<QuizProviderProps> = ({
    children,
    results: initialResults = {},
    count: initialCount = 0,
}) => {
    const [results, setResults] = useState<QuizResults>(initialResults);
    const [count, setCount] = useState<number>(initialCount);

    const handleSetResults = useCallback(
        (newResults: QuizResults) => {
            setResults((prev) => ({
                ...prev,
                ...newResults,
            }));
        },
        [setResults],
    );

    const incrementCount = useCallback(() => {
        setCount((prev) => prev + 1);
    }, [setCount]);

    const clearQuiz = useCallback(() => {
        setResults({});
        setCount(0);
    }, [setResults, setCount]);

    const value = useMemo(
        () => ({
            results,
            count,
            setResults: handleSetResults,
            incrementCount,
            clearQuiz,
        }),
        [results, count, handleSetResults, incrementCount, clearQuiz],
    );

    return <QuizContext value={value}>{children}</QuizContext>;
};

// Hook
export const useQuiz = (): QuizContextValue => {
    const context = use(QuizContext);
    if (context === undefined) {
        throw new Error('useQuiz must be used within a QuizProvider');
    }
    return context;
};

export default QuizContext;
